/**
 * @file planApi.js
 * @description Utility module for handling CRUD operations related to plans.
 *              This module includes functions to fetch, create, update, and delete plans.
 *              Each function uses Axios to interact with the backend API and ensures that 
 *              the necessary authorization headers are included in the requests.
 * @created 28-02-2025
 * @author Govind Dindewad
 */

import axios from "axios";
import apiUrl from "./api";

// -------------------- PLAN CRUD OPERATIONS --------------------

/**
 * Fetches a list of plans based on a search key and platform ID.
 * This function is used to retrieve a list of plans available for a specific platform, 
 * with an optional search keyword to filter the results.
 * 
 * @param {string|null} [searchKey=null] - Optional search keyword for filtering plans. Default is null (no filter).
 * @param {string} platformId - Platform ID for fetching plans associated with a specific platform.
 * @returns {Promise<Object>} - Axios response containing a list of plans or error response.
 */
async function getPlanList(searchKey = null, platformId) {
    const token = window.localStorage.getItem('token');  // Get the authentication token from localStorage.

    try {
        // Send GET request to fetch the list of plans with the given searchKey and platformId.
        const response = await axios.get(`${apiUrl}secure/plan/retain-all-plan?searchKey=${searchKey}&platformId=${platformId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,  // Include the Bearer token for secure API access.
            }
        });
        return response;  // Return the successful response from the API.

    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        // Log error to the console and return the error response if the request fails.
        console.error("Error fetching plan list:", error);
        return error.response;
    }
}

/**
 * Creates a new plan with the provided plan data.
 * This function sends a POST request to the backend to create a new plan using the data passed as an argument.
 * 
 * @param {Object} plan - Data for the new plan to be created. This should contain the necessary fields to define the plan.
 * @returns {Promise<Object>} - Axios response containing the created plan or error response.
 */
async function postPlan(plan) {
    const token = window.localStorage.getItem('token');  // Get the authentication token from localStorage.

    try {
        // Send POST request to create a new plan with the provided plan data.
        const response = await axios.post(`${apiUrl}secure/plan/add-plan`, plan, {
            headers: {
                'Authorization': `Bearer ${token}`,  // Include the Bearer token for secure API access.
            }
        });
        return response;  // Return the successful response from the API.

    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * Updates an existing plan with the provided updated plan data.
 * This function sends a PUT request to the backend to update the details of an existing plan.
 * 
 * @param {Object} plan - Data for the plan to be updated. This should contain the updated details of the plan.
 * @returns {Promise<Object>} - Axios response containing the updated plan or error response.
 */
async function updatePlan(plan) {
    const token = window.localStorage.getItem('token');  // Get the authentication token from localStorage.

    try {
        // Send PUT request to update the plan with the provided updated data.
        const response = await axios.put(`${apiUrl}secure/plan/update-plan`, plan, {
            headers: {
                'Authorization': `Bearer ${token}`,  // Include the Bearer token for secure API access.
            }
        });
        return response;  // Return the successful response from the API.

    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * Deletes an existing plan based on the plan ID.
 * This function sends a DELETE request to the backend to remove a plan from the system.
 * 
 * @param {string} planId - ID of the plan to be deleted.
 * @returns {Promise<Object>} - Axios response confirming deletion or error response.
 */
async function deletePlan(planId) {
    const token = window.localStorage.getItem('token');  // Get the authentication token from localStorage.

    try {
        // Send DELETE request to remove the plan with the specified planId.
        const response = await axios.delete(`${apiUrl}secure/plan/delete-plan?platformPlanId=${planId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,  // Include the Bearer token for secure API access.
            }
        });
        return response;  // Return the successful response from the API.

    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

// Exporting all functions to be used in other modules.
export { getPlanList, postPlan, updatePlan, deletePlan };
